import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardHeader,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useTheme,
} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import { DateFormat } from 'config';
import DescriptionIcon from '@material-ui/icons/Description';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { format, parseISO } from 'date-fns';
import {
  fetchProductStack,
  fetchCspList,
  fetchDormantItemsCount,
  exportDormantItems,
} from 'lib/Api/ProductStack';
import { ProductStackDetails, CspList } from 'lib/Model/ProductStack';
import * as React from 'react';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Skeleton, Alert } from '@material-ui/lab';
import { EmptyView } from 'components/EmptyView';
import { CustomTooltip } from 'components/CustomTooltip';
import { SnackbarContext } from 'components';
import { toMonetary } from 'lib/Utility';
import CspListDialog from './ProductStackCsps';
import ProductStackQuotes from './ProductStackQuotes';
import {
  Launch,
  FiberManualRecord as FiberManualRecordIcon,
} from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
  card: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
  },
  boldTitle: {
    fontWeight: 500,
    padding: theme.spacing(1),
    clear: 'both',
    display: 'block',
    cursor: 'default',
    textAlign: 'left',
  },
  clickable: {
    cursor: 'pointer',
    color: theme.palette.secondary.main,
    textAlign: 'center',
  },
  accordion: {
    '&::before': {
      display: 'none',
    },
    '&.Mui-expanded': {
      margin: 0,
    },
    '& .MuiAccordionSummary-content.Mui-expanded': {
      margin: 0,
    },
    '& .MuiAccordionSummary-root.Mui-expanded': {
      minHeight: 'inherit',
    },
  },
  firstColumn: {
    width: '50%',
  },
  // Quiet labels so the data reads as the loud thing, not the field names.
  labelCell: {
    color: theme.palette.text.secondary,
    fontWeight: theme.typography.fontWeightMedium,
  },
  noListMargin: {
    margin: '0px',
    padding: '0px',
  },
  listControl: {
    display: 'flex',
    justifyContent: 'left',
  },
  accordionSummary: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    '& .MuiTypography-h6': {
      fontWeight: 500,
    },
    '& .MuiAccordionSummary-expandIcon': {
      color: theme.palette.text.secondary,
    },
  },
  headerActions: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  cellResponsive: {
    padding: theme.spacing(1, 2),
    '& .MuiTableRow-root': {
      verticalAlign: '0',
    },
    [theme.breakpoints.down('sm')]: {
      paddingRight: theme.spacing(0.75),
    },
    '&:last-child': {
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0, 0.75),
      },
    },
  },
}));

interface Props {
  companyId: string;
  withRelated: boolean;
}

export const ProductStack: React.FC<Props> = ({ companyId, withRelated }) => {
  const snackbar = React.useContext(SnackbarContext);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [exportLoading, setExportLoading] = React.useState(false);
  const [auditLoading, setAuditLoading] = React.useState(false);
  const [auditCompleted, setAuditCompleted] = React.useState(false);
  const classes = useStyles();
  const [data, setData] = React.useState<ProductStackDetails[]>([]);
  const [CspClients, setCspClients] = React.useState<CspList[]>([]);
  const [CspListDialogOpen, setCspListDialogOpen] = React.useState(false);
  const [, setCspLoading] = React.useState(false);
  const [dormantItems, setDormantItems] = React.useState<number>(0);
  const [quoteListDialogOpen, setQuoteListDialogOpen] = React.useState(false);
  const theme = useTheme();

  const handleOpenCspDialog = async () => {
    setCspLoading(true);
    try {
      const clients = await fetchCspList(companyId);
      setCspClients(clients);
      setCspListDialogOpen(true);
    } catch {
      snackbar.error('Could not load the CSP list. Please try again.');
    } finally {
      setCspLoading(false);
    }
  };

  const handleCloseCspDialog = () => {
    setCspListDialogOpen(false);
  };

  const handleOpenQuotesDialog = () => {
    setQuoteListDialogOpen(true);
  };

  const handleCloseQuotesDialog = () => {
    setQuoteListDialogOpen(false);
  };

  const handleExportDormantItems = async () => {
    setExportLoading(true);
    try {
      await exportDormantItems(companyId);
      snackbar.success('Your audit report has downloaded.');
    } catch {
      snackbar.error('Export failed. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  const handleRunAudit = async () => {
    setAuditLoading(true);
    try {
      const count = await fetchDormantItemsCount(companyId);
      setDormantItems(count);
      setAuditCompleted(true);
    } catch {
      snackbar.error('Could not run the audit. Please try again.');
    } finally {
      setAuditLoading(false);
    }
  };

  const fetchData = React.useCallback(() => {
    setLoading(true);
    setError(false);
    fetchProductStack(companyId, withRelated)
      .then((productStackData) => {
        setData(productStackData);
      })
      .catch(() => {
        setData([]);
        setError(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [companyId, withRelated]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  type ModuleData = {
    unitLicensed: number | undefined;
    unitCost: number | undefined;
    actualLastMonth: number | undefined;
    actualLast3Months: number | undefined;
  };

  type ModuleMapping = {
    [moduleName: string]: ModuleData;
  };

  return (
    <Card className={classes.card}>
      <CardHeader
        title="Product Stack"
        subheader="A summary of the products and services used by the client."
        action={
          <Box className={classes.headerActions}>
            <Button
              color="primary"
              variant="outlined"
              onClick={handleOpenQuotesDialog}
              startIcon={<DescriptionIcon />}
            >
              View Proposals
            </Button>
            <Button
              color="default"
              variant="outlined"
              href="/dashboards#proact"
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<Launch />}
            >
              PROACT Dashboard
            </Button>
            <Button
              color="default"
              variant="outlined"
              href="https://docs.google.com/document/d/1KI-fZ_E-io0DvPgdQ3tx8LPLRgfgo5Ojj5udRQtpKVs/edit#heading=h.8m566ca87l4a"
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<HelpOutlineIcon />}
            >
              Help
            </Button>
          </Box>
        }
      />

      {loading ? (
        <Skeleton variant="rect" style={{ height: 400, width: '100%' }} />
      ) : error ? (
        <Box padding={2}>
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={fetchData}>
                Retry
              </Button>
            }
          >
            Could not load the product stack. Please try again.
          </Alert>
        </Box>
      ) : data.length === 0 ? (
        <EmptyView>
          {
            'Nothing here yet. Expecting some data? Click "Show data at organisation level" to view data at an organisational level.'
          }
        </EmptyView>
      ) : (
        data.map((item) => {
          const hasKonnektData = !!(
            item.knt_tob_expiry ||
            item.latest_konnekt_job ||
            item.latest_konnekt_closed
          );
          const hasTalexioData = !!(
            item.tlx_subscription_company_size ||
            item.tlx_admin_licence_number_of_employees
          );
          const hasJimData = !!(item.first_jim_job || item.jim_package_expiry);
          const hasPmData = !!item.pm_first_invoice;
          const hasSimData = !!item.sim_first_subscription;
          const hasCompetitorData = !!item.competitor_products;

          return (
            <React.Fragment key={item.organisation_id}>
              <>
                <Accordion className={classes.accordion} elevation={0}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    className={classes.accordionSummary}
                  >
                    <Box display="flex" alignItems="center" width="100%">
                      <FiberManualRecordIcon
                        fontSize="inherit"
                        style={{
                          fontSize: '0.7rem',
                          marginRight: theme.spacing(1),
                          color: hasKonnektData
                            ? theme.palette.success.main
                            : theme.palette.action.disabled,
                        }}
                      />
                      <Typography variant="h6">Konnekt</Typography>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails>
                    {hasKonnektData ? (
                      <Table>
                        <TableBody>
                          <TableRow>
                            <TableCell
                              className={classes.cellResponsive}
                              style={{ width: '10%' }}
                            >
                              <div className={classes.listControl}>
                                <CustomTooltip
                                  titleHeader=""
                                  bodyText="Taken from the Konnekt terms in the Intranet. Zendesk is used only when the client has no Intranet terms."
                                  placement="right"
                                  maxWidth={600}
                                  offset="0,15"
                                  useDefaultUnderline={true}
                                  enabledArrow={true}
                                >
                                  Terms Expiry Date
                                </CustomTooltip>
                              </div>
                            </TableCell>
                            <TableCell>
                              {item.knt_tob_expiry
                                ? format(
                                    parseISO(item.knt_tob_expiry),
                                    DateFormat.EXCEL,
                                  )
                                : 'No information. Check if terms expiry date is inputted on Zendesk or in the Intranet.'}
                            </TableCell>{' '}
                          </TableRow>
                          <TableRow>
                            <TableCell className={classes.firstColumn}>
                              Latest Role Opened
                            </TableCell>
                            <TableCell>
                              {item.latest_konnekt_job
                                ? format(
                                    parseISO(item.latest_konnekt_job),
                                    DateFormat.EXCEL,
                                  )
                                : 'N/A'}
                            </TableCell>{' '}
                          </TableRow>
                          <TableRow>
                            <TableCell className={classes.firstColumn}>
                              Latest Role Closing Date
                            </TableCell>
                            <TableCell>
                              {item.latest_konnekt_closed
                                ? format(
                                    parseISO(item.latest_konnekt_closed),
                                    DateFormat.EXCEL,
                                  )
                                : 'N/A'}
                            </TableCell>{' '}
                          </TableRow>
                          <TableRow>
                            <TableCell className={classes.firstColumn}>
                              Vacancies Filled Last 6 Months
                            </TableCell>
                            <TableCell>
                              {item.successful_roles_closed_last_6_months ??
                                'N/A'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className={classes.firstColumn}>
                              Vacancies Opened Last 6 Months
                            </TableCell>
                            <TableCell>
                              {item.roles_opened_last_6_months ?? 'N/A'}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    ) : (
                      <Box width="100%" padding={2}>
                        <Typography variant="body1" gutterBottom>
                          No Konnekt activity data found for this client.
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          Please verify the following:
                        </Typography>
                        <List dense>
                          <ListItem disableGutters>
                            <ListItemIcon style={{ minWidth: '30px' }}>
                              <FiberManualRecordIcon
                                fontSize="inherit"
                                style={{ fontSize: '0.5rem' }}
                              />
                            </ListItemIcon>
                            <ListItemText>
                              Is the ATS profile correctly merged within this
                              client group? Check the Profile Management page
                              for more information.
                            </ListItemText>
                          </ListItem>
                          <ListItem disableGutters>
                            <ListItemIcon style={{ minWidth: '30px' }}>
                              <FiberManualRecordIcon
                                fontSize="inherit"
                                style={{ fontSize: '0.5rem' }}
                              />
                            </ListItemIcon>
                            <ListItemText primary="Have any roles been opened or closed recently?" />
                          </ListItem>
                          <ListItem disableGutters>
                            <ListItemIcon style={{ minWidth: '30px' }}>
                              <FiberManualRecordIcon
                                fontSize="inherit"
                                style={{ fontSize: '0.5rem' }}
                              />
                            </ListItemIcon>
                            <ListItemText primary="Is the Terms of Business expiry date correctly entered in Zendesk or the Intranet?" />
                          </ListItem>
                        </List>
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>

                <Accordion className={classes.accordion} elevation={0}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    className={classes.accordionSummary}
                  >
                    <Box display="flex" alignItems="center" width="100%">
                      <FiberManualRecordIcon
                        fontSize="inherit"
                        style={{
                          fontSize: '0.7rem',
                          marginRight: theme.spacing(1),
                          color: hasTalexioData
                            ? theme.palette.success.main
                            : theme.palette.action.disabled,
                        }}
                      />
                      <Typography variant="h6">Talexio</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {hasTalexioData ? (
                      <Table>
                        <TableBody>
                          {auditCompleted && dormantItems > 0 && (
                            <TableRow>
                              <TableCell colSpan={6}>
                                <Alert
                                  severity="warning"
                                  action={
                                    <Button
                                      startIcon={<GetAppIcon />}
                                      onClick={handleExportDormantItems}
                                      disabled={exportLoading}
                                    >
                                      {exportLoading
                                        ? 'Downloading...'
                                        : 'Download Audit Report'}
                                    </Button>
                                  }
                                >
                                  This client has shown dormant activity on{' '}
                                  {dormantItems} items.
                                </Alert>
                              </TableCell>
                            </TableRow>
                          )}
                          {!auditCompleted && (
                            <TableRow>
                              <TableCell colSpan={6}>
                                <Button
                                  variant="outlined"
                                  color="primary"
                                  onClick={handleRunAudit}
                                  disabled={auditLoading}
                                  fullWidth
                                >
                                  {auditLoading
                                    ? 'Running Audit...'
                                    : 'Run Audit'}
                                </Button>
                              </TableCell>
                            </TableRow>
                          )}
                          {auditCompleted && dormantItems === 0 && (
                            <TableRow>
                              <TableCell colSpan={6}>
                                <Alert severity="success">
                                  Audit completed. No dormant items found for
                                  this client.
                                </Alert>
                              </TableCell>
                            </TableRow>
                          )}
                          <TableRow>
                            <TableCell className={classes.labelCell}>
                              Licensing Mode
                            </TableCell>
                            <TableCell>
                              {item.tlx_is_csp === 1 ? (
                                <div>
                                  Agency or Service Provider
                                  <Button
                                    variant="text"
                                    color="primary"
                                    style={{
                                      background: 'none',
                                    }}
                                    onClick={handleOpenCspDialog}
                                  >
                                    Show CSP List
                                  </Button>
                                </div>
                              ) : item.tlx_is_csp_client === 1 ? (
                                'Client of ' + item.billing_reference
                              ) : item.tlx_is_csp === 0 &&
                                item.tlx_is_csp_client === 0 ? (
                                'Internal HR'
                              ) : (
                                'No Paid Subscription'
                              )}
                            </TableCell>
                            <TableCell className={classes.labelCell}>
                              Admin Users
                            </TableCell>
                            <TableCell colSpan={10}>
                              {item.contact_person
                                ? item.contact_person
                                    .split(',')
                                    .map((admin, index) => (
                                      <li key={index}>{admin.trim()}</li>
                                    ))
                                : 'N/A'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className={classes.labelCell}>
                              Subscription Status
                            </TableCell>
                            <TableCell>
                              <div>
                                {item.tlx_subscription_end_date !== '' &&
                                item.tlx_subscription_end_date !== null ? (
                                  <span>Cancelled</span>
                                ) : item.tlx_next_renewal_date &&
                                  new Date(item.tlx_next_renewal_date) <
                                    new Date() ? (
                                  <span>Expired</span>
                                ) : (item.tlx_subscription_start_date ===
                                    null ||
                                    item.tlx_subscription_start_date === '') &&
                                  (item.tlx_modules === null ||
                                    item.tlx_modules === '') &&
                                  ((item.tlx_subscription_company_size ||
                                    item.tlx_admin_licence_number_of_employees) ??
                                    0) > 0 ? (
                                  <span>Free ATS</span>
                                ) : (item.tlx_hr_essentials_leave_number_of_employees !==
                                    undefined ||
                                    item.tlx_payroll_number_of_employees !==
                                      undefined) &&
                                  item.tlx_actual_employees_last_month !==
                                    undefined ? (
                                  item.tlx_actual_employees_last_month > 0 &&
                                  (item.tlx_actual_employees_last_month ===
                                    item.tlx_hr_essentials_leave_number_of_employees ||
                                    item.tlx_actual_employees_last_month ===
                                      item.tlx_payroll_number_of_employees) ? (
                                    <span
                                      style={{
                                        color: theme.palette.error.main,
                                        fontWeight: 'bold',
                                      }}
                                    >
                                      At Limit
                                    </span>
                                  ) : (item.tlx_actual_employees_last_month ||
                                      item.tlx_admin_licence_number_of_employees) !==
                                    0 ? (
                                    <span>Active</span>
                                  ) : (
                                    'No Paid Subscription'
                                  )
                                ) : (
                                  <span>N/A</span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className={classes.labelCell}>
                              Subscription Start Date
                            </TableCell>
                            <TableCell>
                              {item.tlx_subscription_start_date
                                ? format(
                                    parseISO(item.tlx_subscription_start_date),
                                    'dd-MMM-yy',
                                  )
                                : 'N/A'}
                            </TableCell>
                            <TableCell className={classes.labelCell}>
                              Next Renewal Date
                            </TableCell>
                            <TableCell>
                              {item.tlx_next_renewal_date
                                ? format(
                                    parseISO(item.tlx_next_renewal_date),
                                    'dd-MMM-yy',
                                  )
                                : 'N/A'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className={classes.labelCell}>
                              Team Voice Start Date
                            </TableCell>
                            <TableCell>
                              {item.talexio_tv_start_date
                                ? format(
                                    parseISO(item.talexio_tv_start_date),
                                    'dd-MMM-yy',
                                  )
                                : 'N/A'}
                            </TableCell>
                            <TableCell className={classes.labelCell}>
                              Support Package
                            </TableCell>
                            <TableCell>
                              {toMonetary(item.tlx_support_price || 'N/A')}
                            </TableCell>
                          </TableRow>
                          <TableRow
                            style={{ backgroundColor: theme.palette.grey[100] }}
                          >
                            <TableCell className={classes.labelCell}>
                              Module
                            </TableCell>
                            <TableCell className={classes.labelCell}>
                              Licensed by
                            </TableCell>
                            <TableCell className={classes.labelCell}>
                              Units Licensed
                            </TableCell>
                            <TableCell className={classes.labelCell}>
                              Unit Cost (EUR) per Month
                            </TableCell>
                            <TableCell className={classes.labelCell}>
                              Actual Units (Last Month)
                            </TableCell>
                            <TableCell className={classes.labelCell}>
                              Actual Units (Last 3 Months)
                            </TableCell>
                          </TableRow>
                          {[
                            'HR Essentials & Leave Management',
                            'Payroll, Leave Management & HR Essentials',
                            'HR - Time & Attendance',
                            'HR - Performance',
                            'HR - Training',
                            'ATS',
                            'Cloud Device Management',
                          ].map((module) => {
                            let moduleMapping: ModuleMapping = {};

                            if (
                              'tlx_hr_essentials_leave_number_of_employees' in
                              item
                            ) {
                              moduleMapping[
                                'HR Essentials & Leave Management'
                              ] = {
                                unitLicensed:
                                  item.tlx_hr_essentials_leave_number_of_employees,
                                unitCost: item.tlx_hr_essentials_leave_price,
                                actualLastMonth:
                                  item.tlx_actual_employees_last_month,
                                actualLast3Months:
                                  item.tlx_actual_employees_last_3_month,
                              };
                            }

                            if ('tlx_payroll_number_of_employees' in item) {
                              moduleMapping[
                                'Payroll, Leave Management & HR Essentials'
                              ] = {
                                unitLicensed:
                                  item.tlx_payroll_number_of_employees,
                                unitCost: item.tlx_payroll_price,
                                actualLastMonth:
                                  'HR Essentials & Leave Management' in
                                    moduleMapping &&
                                  moduleMapping[
                                    'HR Essentials & Leave Management'
                                  ].unitLicensed === 0 &&
                                  item.tlx_payroll_number_of_employees !== 0 &&
                                  item.tlx_payroll_number_of_employees !== null
                                    ? moduleMapping[
                                        'HR Essentials & Leave Management'
                                      ].actualLastMonth
                                    : undefined,
                                actualLast3Months:
                                  'HR Essentials & Leave Management' in
                                    moduleMapping &&
                                  moduleMapping[
                                    'HR Essentials & Leave Management'
                                  ].unitLicensed === 0 &&
                                  item.tlx_payroll_number_of_employees !== 0 &&
                                  item.tlx_payroll_number_of_employees !== null
                                    ? moduleMapping[
                                        'HR Essentials & Leave Management'
                                      ].actualLast3Months
                                    : undefined,
                              };

                              if (
                                'HR Essentials & Leave Management' in
                                  moduleMapping &&
                                moduleMapping[
                                  'HR Essentials & Leave Management'
                                ].unitLicensed === 0 &&
                                item.tlx_payroll_number_of_employees !== 0 &&
                                item.tlx_payroll_number_of_employees !== null
                              ) {
                                moduleMapping[
                                  'HR Essentials & Leave Management'
                                ].actualLastMonth = undefined;
                                moduleMapping[
                                  'HR Essentials & Leave Management'
                                ].actualLast3Months = undefined;
                              }
                            }

                            moduleMapping['HR - Time & Attendance'] = {
                              unitLicensed:
                                item.tlx_time_and_attendance_number_of_employees,
                              unitCost: item.tlx_time_and_attendance_price,
                              actualLastMonth:
                                item.tlx_actual_ta_employees_last_month,
                              actualLast3Months:
                                item.tlx_actual_ta_employees_last_3_month,
                            };
                            moduleMapping['HR - Performance'] = {
                              unitLicensed:
                                item.tlx_performance_number_of_employees,
                              unitCost: item.tlx_performance_price,
                              actualLastMonth: undefined,
                              actualLast3Months: undefined,
                            };
                            moduleMapping['HR - Training'] = {
                              unitLicensed:
                                item.tlx_training_management_number_of_employees,
                              unitCost: item.tlx_training_management_price,
                              actualLastMonth: undefined,
                              actualLast3Months: undefined,
                            };
                            moduleMapping['ATS'] = {
                              unitLicensed:
                                (item.tlx_admin_licence_number_of_employees ||
                                  0) +
                                (item.tlx_additional_admin_licence_number_of_employees ||
                                  0),
                              unitCost: item.tlx_admin_licence_price,
                              actualLastMonth: undefined,
                              actualLast3Months: undefined,
                            };

                            moduleMapping['Cloud Device Management'] = {
                              unitLicensed:
                                item.tlx_cloud_device_manage_number_of_employees,
                              unitCost: item.tlx_cloud_device_manage_price,
                              actualLastMonth: undefined,
                              actualLast3Months: undefined,
                            };

                            if (module in moduleMapping) {
                              const moduleData = moduleMapping[module];
                              const isLicensedByAdmin = ['ATS'].includes(
                                module,
                              );
                              return (
                                <TableRow key={module}>
                                  <TableCell>{module}</TableCell>
                                  <TableCell>
                                    {isLicensedByAdmin
                                      ? 'HR Managers'
                                      : 'Users'}
                                  </TableCell>
                                  <TableCell>
                                    {moduleData.unitLicensed ?? '-'}
                                  </TableCell>
                                  <TableCell>
                                    {moduleData.unitCost
                                      ? `€${moduleData.unitCost.toFixed(2)}`
                                      : '-'}
                                  </TableCell>
                                  <TableCell>
                                    {moduleData.actualLastMonth ?? '-'}
                                  </TableCell>
                                  <TableCell>
                                    {moduleData.actualLast3Months ?? '-'}
                                  </TableCell>
                                </TableRow>
                              );
                            }
                            return null;
                          })}
                        </TableBody>
                      </Table>
                    ) : (
                      <Box width="100%" padding={2}>
                        <Typography variant="body1" gutterBottom>
                          No Talexio subscription data found for this client.
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          Please verify the following:
                        </Typography>
                        <List dense>
                          <ListItem disableGutters>
                            <ListItemIcon style={{ minWidth: '30px' }}>
                              <FiberManualRecordIcon
                                fontSize="inherit"
                                style={{ fontSize: '0.5rem' }}
                              />
                            </ListItemIcon>
                            <ListItemText>
                              Is the Talexio profile correctly merged within
                              this client group? Check the Profile Management
                              page for more information.
                            </ListItemText>
                          </ListItem>
                          <ListItem disableGutters>
                            <ListItemIcon style={{ minWidth: '30px' }}>
                              <FiberManualRecordIcon
                                fontSize="inherit"
                                style={{ fontSize: '0.5rem' }}
                              />
                            </ListItemIcon>
                            <ListItemText primary="Does the client have an active Talexio subscription (paid or free ATS)?" />
                          </ListItem>
                          <ListItem disableGutters>
                            <ListItemIcon style={{ minWidth: '30px' }}>
                              <FiberManualRecordIcon
                                fontSize="inherit"
                                style={{ fontSize: '0.5rem' }}
                              />
                            </ListItemIcon>
                            <ListItemText primary="Are the subscription details (company size, admin licenses) correctly entered in the system?" />
                          </ListItem>
                        </List>
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>

                <Accordion className={classes.accordion} elevation={0}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    className={classes.accordionSummary}
                  >
                    <Box display="flex" alignItems="center" width="100%">
                      <FiberManualRecordIcon
                        fontSize="inherit"
                        style={{
                          fontSize: '0.7rem',
                          marginRight: theme.spacing(1),
                          color: hasJimData
                            ? theme.palette.success.main
                            : theme.palette.action.disabled,
                        }}
                      />
                      <Typography variant="h6">Jobsinmalta</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {hasJimData ? (
                      <Box width="100%">
                        <Table>
                          <TableBody>
                            <TableRow>
                              <TableCell className={classes.firstColumn}>
                                First Listing Opened
                              </TableCell>
                              <TableCell>
                                {item.first_jim_job
                                  ? format(
                                      parseISO(item.first_jim_job),
                                      DateFormat.EXCEL,
                                    )
                                  : 'N/A'}
                              </TableCell>{' '}
                            </TableRow>
                            <TableRow>
                              <TableCell className={classes.firstColumn}>
                                Latest Listing Created
                              </TableCell>
                              <TableCell>
                                {item.latest_jim_job
                                  ? format(
                                      parseISO(item.latest_jim_job),
                                      DateFormat.EXCEL,
                                    )
                                  : 'N/A'}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell className={classes.firstColumn}>
                                Latest Listing Published
                              </TableCell>
                              <TableCell>
                                {item.jim_latest_publishing
                                  ? format(
                                      parseISO(item.jim_latest_publishing),
                                      DateFormat.EXCEL,
                                    )
                                  : 'N/A'}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell className={classes.firstColumn}>
                                Wallet Balance
                              </TableCell>
                              <TableCell>
                                {item.balance === null ||
                                typeof item.balance === 'undefined' ||
                                item.balance === ''
                                  ? 'N/A'
                                  : Number(item.balance) > 1000
                                  ? 'Unlimited'
                                  : item.balance}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell className={classes.firstColumn}>
                                <CustomTooltip
                                  titleHeader=""
                                  bodyText="Note: This date refers to the expiry of the latest bulk or unlimited package. For an overview on the client's unlimited package, please refer to the Wallet section."
                                  placement="right"
                                  maxWidth={600}
                                  offset="0,15"
                                  useDefaultUnderline={true}
                                  enabledArrow={true}
                                >
                                  Package Expiry Date
                                </CustomTooltip>
                              </TableCell>
                              <TableCell>
                                {item.jim_package_expiry
                                  ? format(
                                      parseISO(item.jim_package_expiry),
                                      DateFormat.EXCEL,
                                    )
                                  : 'N/A'}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                        <Typography
                          variant="caption"
                          display="block"
                          style={{ marginTop: 8, fontStyle: 'italic' }}
                        >
                          Note: If this company is a subcompany of another
                          client, click the "Show data at organisation level"
                          button at the top of the screen to see the package
                          statistics. Alternatively, visit the Client Wallet for
                          more information.
                        </Typography>
                      </Box>
                    ) : (
                      <Box width="100%" padding={2}>
                        <Typography variant="body1" gutterBottom>
                          No Jobsinmalta activity data found for this client.
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          Please verify the following:
                        </Typography>
                        <List dense>
                          <ListItem disableGutters>
                            <ListItemIcon style={{ minWidth: '30px' }}>
                              <FiberManualRecordIcon
                                fontSize="inherit"
                                style={{ fontSize: '0.5rem' }}
                              />
                            </ListItemIcon>
                            <ListItemText>
                              Is the Jobsinmalta profile correctly merged within
                              this client group? Check the Profile Management
                              page for more information.
                            </ListItemText>
                          </ListItem>
                          <ListItem disableGutters>
                            <ListItemIcon style={{ minWidth: '30px' }}>
                              <FiberManualRecordIcon
                                fontSize="inherit"
                                style={{ fontSize: '0.5rem' }}
                              />
                            </ListItemIcon>
                            <ListItemText primary="Has the client ever posted a job listing on Jobsinmalta?" />
                          </ListItem>
                          <ListItem disableGutters>
                            <ListItemIcon style={{ minWidth: '30px' }}>
                              <FiberManualRecordIcon
                                fontSize="inherit"
                                style={{ fontSize: '0.5rem' }}
                              />
                            </ListItemIcon>
                            <ListItemText primary="Does the client have an active or expired package (check Wallet section or package expiry date)?" />
                          </ListItem>
                        </List>
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>

                <Accordion className={classes.accordion} elevation={0}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    className={classes.accordionSummary}
                  >
                    <Box display="flex" alignItems="center" width="100%">
                      <FiberManualRecordIcon
                        fontSize="inherit"
                        style={{
                          fontSize: '0.7rem',
                          marginRight: theme.spacing(1),
                          color: hasPmData
                            ? theme.palette.success.main
                            : theme.palette.action.disabled,
                        }}
                      />
                      <Typography variant="h6">Konnekt Payroll</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {hasPmData ? (
                      <Table>
                        <TableBody>
                          <TableRow>
                            <TableCell className={classes.firstColumn}>
                              First Invoice
                            </TableCell>
                            <TableCell>
                              {item.pm_first_invoice
                                ? format(
                                    parseISO(item.pm_first_invoice),
                                    DateFormat.EXCEL,
                                  )
                                : 'N/A'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className={classes.firstColumn}>
                              Latest Invoice
                            </TableCell>
                            <TableCell>
                              {item.pm_latest_invoice
                                ? format(
                                    parseISO(item.pm_latest_invoice),
                                    DateFormat.EXCEL,
                                  )
                                : 'N/A'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className={classes.firstColumn}>
                              Subscription Status
                            </TableCell>
                            <TableCell>
                              {item.pm_4_months_before_expiry === 1
                                ? 'About to Expire'
                                : item.pm_4_months_before_expiry === 0
                                ? 'Active'
                                : 'N/A'}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    ) : (
                      <Box width="100%" padding={2}>
                        <Typography variant="body1" gutterBottom>
                          No Konnekt Payroll activity data found for this
                          client.
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          Please verify the following:
                        </Typography>
                        <List dense>
                          <ListItem disableGutters>
                            <ListItemIcon style={{ minWidth: '30px' }}>
                              <FiberManualRecordIcon
                                fontSize="inherit"
                                style={{ fontSize: '0.5rem' }}
                              />
                            </ListItemIcon>
                            <ListItemText>
                              Is the client's profile correctly linked or merged
                              within this client group? Check the Profile
                              Management page for more information.
                            </ListItemText>
                          </ListItem>
                          <ListItem disableGutters>
                            <ListItemIcon style={{ minWidth: '30px' }}>
                              <FiberManualRecordIcon
                                fontSize="inherit"
                                style={{ fontSize: '0.5rem' }}
                              />
                            </ListItemIcon>
                            <ListItemText primary="Has the client ever been invoiced by Konnekt Payroll (check for first invoice date)?" />
                          </ListItem>
                        </List>
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>

                <Accordion className={classes.accordion} elevation={0}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    className={classes.accordionSummary}
                  >
                    <Box display="flex" alignItems="center" width="100%">
                      <FiberManualRecordIcon
                        fontSize="inherit"
                        style={{
                          fontSize: '0.7rem',
                          marginRight: theme.spacing(1),
                          color: hasSimData
                            ? theme.palette.success.main
                            : theme.palette.action.disabled,
                        }}
                      />
                      <Typography variant="h6">Salariesinmalta</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {hasSimData ? (
                      <Table>
                        <TableBody>
                          <TableRow>
                            <TableCell className={classes.firstColumn}>
                              First Subscription
                            </TableCell>
                            <TableCell>
                              {item.sim_first_subscription
                                ? format(
                                    parseISO(item.sim_first_subscription),
                                    DateFormat.EXCEL,
                                  )
                                : 'N/A'}
                            </TableCell>{' '}
                          </TableRow>
                          <TableRow>
                            <TableCell className={classes.firstColumn}>
                              Target Renewal Date
                            </TableCell>
                            <TableCell>
                              {item.sim_target_renewal_date
                                ? format(
                                    parseISO(item.sim_target_renewal_date),
                                    DateFormat.EXCEL,
                                  )
                                : 'N/A'}
                            </TableCell>{' '}
                          </TableRow>

                          <TableRow>
                            <TableCell className={classes.firstColumn}>
                              Latest Expiry Date
                            </TableCell>
                            <TableCell>
                              {item.sim_subscription_expiry
                                ? format(
                                    parseISO(item.sim_subscription_expiry),
                                    DateFormat.EXCEL,
                                  )
                                : 'N/A'}
                            </TableCell>{' '}
                          </TableRow>
                          <TableRow>
                            <TableCell className={classes.firstColumn}>
                              Latest Subscription Type
                            </TableCell>
                            <TableCell>
                              {item.sim_subscription_type === '1'
                                ? 'Free Plan'
                                : item.sim_subscription_type === '2'
                                ? 'Premium Plan'
                                : 'N/A'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className={classes.firstColumn}>
                              Subscription Status
                            </TableCell>
                            <TableCell>
                              {item.sim_subscription_expiry &&
                              new Date(item.sim_subscription_expiry) <
                                new Date()
                                ? 'Expired'
                                : item.sim_4_months_before_expiry === 1 &&
                                  item.sim_subscription_expiry &&
                                  new Date() <
                                    new Date(item.sim_subscription_expiry)
                                ? 'About to Expire'
                                : 'Active'}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    ) : (
                      <Box width="100%" padding={2}>
                        <Typography variant="body1" gutterBottom>
                          No Salariesinmalta subscription data found for this
                          client.
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          Please verify the following:
                        </Typography>
                        <List dense>
                          <ListItem disableGutters>
                            <ListItemIcon style={{ minWidth: '30px' }}>
                              <FiberManualRecordIcon
                                fontSize="inherit"
                                style={{ fontSize: '0.5rem' }}
                              />
                            </ListItemIcon>
                            <ListItemText>
                              Is the Salariesinmalta profile correctly linked or
                              merged with this client group? Click here to visit
                              the profile management page.
                            </ListItemText>
                          </ListItem>
                          <ListItem disableGutters>
                            <ListItemIcon style={{ minWidth: '30px' }}>
                              <FiberManualRecordIcon
                                fontSize="inherit"
                                style={{ fontSize: '0.5rem' }}
                              />
                            </ListItemIcon>
                            <ListItemText primary="Has the client ever subscribed to Salariesinmalta (check for first subscription date)?" />
                          </ListItem>
                        </List>
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>

                {hasCompetitorData ? (
                  <Accordion className={classes.accordion} elevation={0}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      className={classes.accordionSummary}
                    >
                      <Box display="flex" alignItems="center" width="100%">
                        <FiberManualRecordIcon
                          fontSize="inherit"
                          style={{
                            fontSize: '0.7rem',
                            marginRight: theme.spacing(1),
                            color: theme.palette.success.main,
                          }}
                        />
                        <Typography variant="h6">
                          Competitor Products
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Table>
                        <TableBody>
                          <TableRow>
                            <TableCell className={classes.firstColumn}>
                              Products
                            </TableCell>
                            <TableCell>{item.competitor_products}</TableCell>{' '}
                          </TableRow>
                        </TableBody>
                      </Table>
                    </AccordionDetails>
                  </Accordion>
                ) : null}
              </>
            </React.Fragment>
          );
        })
      )}
      <CspListDialog
        open={CspListDialogOpen}
        cspClients={CspClients}
        onClose={handleCloseCspDialog}
        loading={loading}
      />
      <ProductStackQuotes
        open={quoteListDialogOpen}
        onClose={handleCloseQuotesDialog}
        companyId={companyId}
      />
    </Card>
  );
};
